'use client';
import styles from './locationselect.module.scss';
import { EarthAmericasRegularIcon, MarkerIcon, CameraHomeRegularIcon } from '../Icons';
import { Address } from '@shared/types';
import { FunctionComponent, useCallback, useId, useMemo } from 'react';
import { getUniqueOccurenceAddresses } from './helpers';
import { SingleSelect } from '../SingleSelect';
import {
    PublicBlendedCourse,
    PublicClassCourse,
    PublicOccurrence,
} from '@cademy-package/sdk-component-types';

export type LocationSelectOption =
    | {
          key: 'none';
          value: 'all';
          label: 'All Locations' | 'All Locations & Online';
      }
    | {
          key: 'delivery';
          value: 'online';
          label: 'Online';
      }
    | {
          key: 'address';
          address: {
              city: string;
              street?: string;
          };
          value: string;
          label: string;
      };

const getAddressOptions = (
    courseAddress: Address,
    occurrences: Array<PublicOccurrence>
): Array<LocationSelectOption> => {
    const uniqueAddresses = getUniqueOccurenceAddresses(courseAddress, occurrences);
    return uniqueAddresses.map((address) => {
        const addressesInSameCity = uniqueAddresses.filter(
            (filterAddress) => address.city === filterAddress.city
        );
        // if only 1 address in that city, show the city, else show the street too.
        if (addressesInSameCity.length === 1) {
            return {
                key: 'address',
                value: address.city.toLowerCase(),
                address: { city: address.city, label: address.city },
                label: address.city,
            };
        } else {
            const label = `${address.city}${address.street ? `, ${address.street}` : ''}`;
            return {
                key: 'address',
                value: `${address.city}, ${address.street}`.toLowerCase(),
                address: {
                    city: address.city,
                    street: address.street,
                },
                label: label,
            };
        }
    });
};

type LocationSelectProps = {
    course: PublicClassCourse | PublicBlendedCourse;
    occurrences: Array<PublicOccurrence>;
    value: string | null;
    onChange: (filter: LocationSelectOption) => any;
};

const defaultSelectedOption: LocationSelectOption = {
    key: 'none',
    value: 'all',
    label: 'All Locations',
};

const LocationSelect: FunctionComponent<LocationSelectProps> = ({
    course,
    occurrences,
    value,
    onChange,
}) => {
    const id = useId();

    const locations = useMemo((): Array<LocationSelectOption> => {
        const hasOnline = occurrences.some((occ) => occ.delivery === 'online');
        const addressForCourse =
            course.address && ['in-person', 'hybrid'].includes(course.delivery)
                ? course.address
                : {};
        const addressOptions = getAddressOptions(addressForCourse, occurrences);
        const onlineOption: LocationSelectOption = {
            key: 'delivery',
            value: 'online',
            label: 'Online',
        };
        return [
            {
                ...defaultSelectedOption,
                label: hasOnline ? 'All Locations & Online' : 'All Locations',
            },
            ...(hasOnline ? [onlineOption] : []),
            ...addressOptions,
        ];
    }, [course.address, course.delivery, occurrences]);

    const selectedValue: LocationSelectOption = useMemo(() => {
        const matchingValues: Array<LocationSelectOption> = locations.filter(
            (location: LocationSelectOption) => location.value === value
        );
        if (matchingValues.length > 0) {
            return matchingValues[0];
        }
        return locations[0];
    }, [locations, value]);

    const onSelectChange = useCallback(
        (newValue: LocationSelectOption) => {
            onChange(newValue);
        },
        [onChange]
    );

    if (['on-request', 'on-demand'].includes(course.type)) return null;

    if (locations.length <= 2) {
        return null;
    }

    return (
        <section className={styles.locationsDropdown}>
            <div id={`${id}-description`} className="screen_reader_hidden">
                Filter the available options by location
            </div>
            <SingleSelect
                aria-describedby={`${id}-description`}
                aria-label="Location filter"
                options={locations}
                labelKey={'label'}
                valueKey={'value'}
                value={selectedValue}
                onChange={onSelectChange}
            >
                {(location: LocationSelectOption) => {
                    if (!location) {
                        return null;
                    }
                    const icons = {
                        delivery: CameraHomeRegularIcon,
                        address: MarkerIcon,
                        none: EarthAmericasRegularIcon,
                    };
                    const Icon = icons[location.key];
                    return (
                        <div className={styles.option}>
                            <Icon height={14} width={14} />
                            <span>{location.label}</span>
                        </div>
                    );
                }}
            </SingleSelect>
        </section>
    );
};

export default LocationSelect;
